import type React from 'react'
import {useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import {BreakpointSize, Image, useWindowSize} from '@primer/react-brand/esm'

import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useIntersectionObserver} from '@github-ui/swp-core/lib/hooks/use-intersection-observer'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import useVideo from '../../hooks/useVideo'
import {wait} from '../../utils/time'
import {BREAKPOINT_DESKTOP_MIN_HEIGHT} from '../../utils/responsive'

import PlayButton, {TRANSITION_DURATION, type PlayButtonProps} from '../PlayButton/PlayButton'

import useMotion from './HeroCarousel.motion'
import {SCREENS, HeroScreenId} from './HeroCarousel.data'

export interface HeroCarouselProps {
  currentScreenIndex: number
  onAutoPlayTimeout: (newScreenIndex: number) => void
  carouselAriaLabel?: string
  playButtonAriaLabel?: PlayButtonProps['ariaLabel']
  isVisible?: boolean
}

const defaultProps: Partial<HeroCarouselProps> = {
  isVisible: true,
}

const screenSupportsDarkTheme = new Set<HeroScreenId>([HeroScreenId.automate, HeroScreenId.secure])

const HeroCarousel: React.FC<HeroCarouselProps> = props => {
  const initializedProps = {...defaultProps, ...props}
  const {currentScreenIndex, onAutoPlayTimeout, carouselAriaLabel, playButtonAriaLabel, isVisible} = initializedProps

  const {isBrowser} = useRuntimeEnvironment()
  const shouldReduceMotion = usePrefersReducedMotion()
  const [isVisualVisible, setIsVisualVisible] = useState<boolean>(true)
  const [visibleScreenIndex, setVisibleScreenIndex] = useState<number>(currentScreenIndex)
  const [isPaused, setIsPaused] = useState<boolean>(false)
  const [hasTimelineEnded, setHasTimelineEnded] = useState<boolean>(false)

  // Pause immediately when the user's reduced-motion preference turns on
  const [prevShouldReduceMotion, setPrevShouldReduceMotion] = useState(shouldReduceMotion)
  if (shouldReduceMotion !== prevShouldReduceMotion) {
    setPrevShouldReduceMotion(shouldReduceMotion)
    if (shouldReduceMotion) setIsPaused(true)
  }

  const currentScreenIndexRef = useRef<number>(currentScreenIndex)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const visualRef = useRef<HTMLDivElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const windowSize = useWindowSize()

  const isDesktopView = useMemo(
    () =>
      [BreakpointSize.MEDIUM, BreakpointSize.LARGE, BreakpointSize.XLARGE, BreakpointSize.XXLARGE].includes(
        windowSize.currentBreakpointSize!,
      ),
    [windowSize],
  )
  const visibleScreen = useMemo(() => SCREENS[visibleScreenIndex], [visibleScreenIndex])
  const visibleScreenDuration = useMemo(
    () =>
      visibleScreen?.visuals.reduce((previousValue, visual) => {
        const viewportVisual = !isDesktopView && visual.mobile ? visual.mobile : visual.desktop

        return previousValue + viewportVisual.duration
      }, 0) || 0,
    [visibleScreen, isDesktopView],
  )

  const onTimelineEnded = useCallback(() => {
    setHasTimelineEnded(true)
  }, [])

  const {
    show: showTimeline,
    resume: resumeTimeline,
    pause: pauseTimeline,
    finish: finishTimeline,
  } = useMotion({visual: visualRef, onFinish: onTimelineEnded, isDesktopView})

  const {
    addListeners: addVideoListeners,
    removeListeners: removeVideoListeners,
    onVideoTimeUpdate,
  } = useVideo({videoRef, isPaused})

  const isSmallHeight = useMemo(
    () => (windowSize?.height && windowSize.height < BREAKPOINT_DESKTOP_MIN_HEIGHT) || false,
    [windowSize],
  )

  const {isIntersecting} = useIntersectionObserver(
    wrapperRef,
    {threshold: {mobile: 0.33, desktop: 0.5}, isOnce: true},
    !isSmallHeight && isDesktopView,
  )

  const onVisualTimeout = useCallback(() => {
    onAutoPlayTimeout((currentScreenIndexRef.current + 1) % SCREENS.length)
  }, [onAutoPlayTimeout])

  const onPlayStateChange = useCallback((shouldPause: boolean) => {
    setIsPaused(shouldPause)
    if (!shouldPause) setHasTimelineEnded(false)
  }, [])

  const onCurrentScreenIndexChange = useEffectEvent(() => {
    currentScreenIndexRef.current = currentScreenIndex

    if (!isVisible || !isIntersecting) return

    // Add delay to allow the transition to complete
    const proceed = async () => {
      if (shouldReduceMotion) {
        removeVideoListeners()
        setVisibleScreenIndex(currentScreenIndexRef.current)
        return
      }

      setIsVisualVisible(false)
      await wait((TRANSITION_DURATION / 2) * 1000)

      removeVideoListeners()

      setVisibleScreenIndex(currentScreenIndexRef.current)
      await wait((TRANSITION_DURATION / 2) * 1000)

      setIsVisualVisible(true)
    }

    proceed()
  })

  useEffect(() => {
    onCurrentScreenIndexChange()
  }, [currentScreenIndex])

  const onVisibleScreenIndexChange = useEffectEvent(() => {
    if (!shouldReduceMotion && visibleScreen && isVisible && isIntersecting) {
      showTimeline(visibleScreen.id)
      addVideoListeners()
    }
  })

  useEffect(() => {
    onVisibleScreenIndexChange()
  }, [visibleScreenIndex])

  const onIsPausedChange = useEffectEvent(() => {
    if (!isIntersecting || !isVisible) return
    if (isPaused) pauseTimeline()
    else resumeTimeline()
  })

  useEffect(() => {
    onIsPausedChange()
  }, [isPaused])

  const onWindowSizeChange = useEffectEvent(() => {
    if (!shouldReduceMotion && visibleScreen) {
      // Reposition elements when the window resizes
      finishTimeline(visibleScreen.id)
    }
  })

  useEffect(() => {
    onWindowSizeChange()
  }, [windowSize])

  const onUnmount = useEffectEvent(() => {
    removeVideoListeners()
  })

  useEffect(() => {
    return () => onUnmount()
  }, [])

  return (
    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
    <div ref={wrapperRef} className="lp-HeroCarousel">
      {carouselAriaLabel && <h2 className="visually-hidden">{carouselAriaLabel}</h2>}

      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <div className="lp-HeroCarousel-background">
        <div />
        <div />
      </div>

      {!shouldReduceMotion && (
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        <div className="lp-HeroCarousel-playButton">
          <PlayButton
            playKey={currentScreenIndex}
            duration={visibleScreenDuration}
            onTimeout={onVisualTimeout}
            onPlayStateChange={onPlayStateChange}
            ariaLabel={playButtonAriaLabel}
            shouldAutoForward={false}
            hasLinkedTimelineEnded={hasTimelineEnded}
            isDarkTheme={visibleScreen && isDesktopView && screenSupportsDarkTheme.has(visibleScreen.id)}
          />
        </div>
      )}

      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <div className={`lp-HeroCarousel-container ${!isVisible ? 'lp-HeroCarousel-container--hidden' : ''}`}>
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <div className="lp-HeroCarousel-content">
          <div
            ref={visualRef}
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            className={`lp-HeroCarousel-visual ${!isVisualVisible ? 'lp-HeroCarousel-visual--hidden' : ''}`}
          >
            {visibleScreen?.visuals.length
              ? visibleScreen.visuals.map((visuals, index) => {
                  const visual = !isDesktopView && visuals.mobile ? visuals.mobile : visuals.desktop

                  const key = `visual-${visibleScreen.id}-${index}`
                  const src = visual.url
                  const poster = visual.poster

                  return visual.type === 'video' ? (
                    <div key={key}>
                      <div className="sr-only" id={`${key}-desc`}>
                        {visual.alt}
                      </div>
                      <video
                        ref={videoRef}
                        muted
                        playsInline
                        aria-describedby={`${key}-desc`}
                        src={isBrowser ? src : undefined}
                        poster={isBrowser ? poster : undefined}
                        onTimeUpdate={onVideoTimeUpdate}
                        onEnded={onTimelineEnded}
                        style={visual.zIndex ? {zIndex: visual.zIndex} : undefined}
                        className={visual.className}
                      />
                    </div>
                  ) : (
                    <Image
                      key={key}
                      src={src}
                      alt={visual.alt}
                      aria-hidden={visual.ariaHidden ? true : undefined}
                      style={visual.zIndex ? {zIndex: visual.zIndex} : undefined}
                      className={visual.className}
                    />
                  )
                })
              : null}

            {visibleScreen?.id === HeroScreenId.plan && (
              /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
              <div className="lp-HeroCarousel-visual-planMenu">
                <div />
                <div />
                <div />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default HeroCarousel
